import { Box, styled, SxProps } from '@mui/system';
import {
  MenuItem,
  TextField,
  Theme,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material';
import MuiPhoneNumber from 'material-ui-phone-number';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { StyledSelect, styledSelectMenuProps } from './StyledFormInputs';
import { QuestionOption } from './models/Question';
import DraggableListComponent from './dragAndDrop/DraggableListComponent';
import ImageOptionComponent from './ImageOptionComponent';
import PollQuestionComponent from './PollQuestionComponent';
import GroupDropdownComponent from './GroupDropdownComponent';
import { GroupAutocompleteComponent } from './QuestionComponentParts/GroupAutocompleteComponent';
import { BusinessTrade } from './models/Member';
import { QuestionComponentAutocomplete } from './QuestionComponentParts/QuestionComponentAutocomplete';

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
})) as typeof TextField;

export const useStylesForQuestionComponent = makeStyles((theme: Theme) => ({
  root: {
    '&.Mui-focused .MuiInputLabel-outlined': {
      color: theme.palette.common.black,
    },
  },
  inputRoot: {
    color: `${theme.palette.common.black} !important`,
    backgroundColor: theme.palette.common.white,
    borderRadius: '13px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.common.white,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.common.white} !important`,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.common.white} !important`,
    },
    '&.Mui-focused': {
      borderColor: `${theme.palette.common.white} !important`,
    },
    '&.MuiOutlinedInput-input': {
      borderRadius: '13px !important',
    },
  },
}));

type InputProps = {
  inputValue: unknown
  type: string
  placeholderText: string
  subTitle?: string
  options?: QuestionOption[]
  selectedOptions?: BusinessTrade[]
  meta?: {
    [k: string]: unknown
  }
  onChange: (...event: unknown[]) => void
  onBlur?: () => void
  radioButtonSelectDefaultValues?: QuestionOption | QuestionOption[]
  groupAutoComplete?: boolean
  textFieldDisabled?: boolean
  borderStyle?: boolean
  isOutlined?: boolean
  calledFromOnboarding?: boolean
  showAutoCompletePlaceholderText?: boolean
  ['data-testid']?: string;
  optionalRadioBtnSelectRadioStyles?: SxProps<Theme>;
  optionalRadioBtnSelectFormLabelStyles?:SxProps<Theme>;
}

const QuestionComponent = ({
  type,
  placeholderText,
  meta,
  radioButtonSelectDefaultValues,
  groupAutoComplete,
  inputValue,
  onChange,
  onBlur,
  subTitle,
  textFieldDisabled,
  options = [],
  selectedOptions = [],
  borderStyle,
  isOutlined,
  calledFromOnboarding,
  showAutoCompletePlaceholderText,
  optionalRadioBtnSelectRadioStyles,
  optionalRadioBtnSelectFormLabelStyles,
  'data-testid': dataTestId,
}: InputProps) => {
  const { t } = useTranslation();
  const classes = useStylesForQuestionComponent();
  const theme = useTheme();

  const getTranslation = (input: string) => {
    const key = `onboarding.questionPlaceholder.${input}`;
    return `${t(key)}`;
  };

  const getPhoneNumberValue = (input: string) => {
    if (input && !(input.startsWith('+') || input.startsWith('1'))) {
      return `+1${input}`;
    }
    return input;
  };

  const getDefaultValue = (oldValue:string) => {
    const oldToNewValueMap: {
      [oldValueName: string]: string;
    } = {
      // old value name    // new value name
      'Scale my business': 'Grow my business to meet my goals',
      'Control cash flow': 'Manage my finances and monitor costs',
      'Better work/life balance': 'Find a balance between work and life after work',
    };

    return oldToNewValueMap[oldValue] || oldValue;
  };

  switch (type) {
    case 'open':
      switch (meta?.behavior) {
        case 'phoneInput':
          return (
            <MuiPhoneNumber
              defaultCountry={'us'}
              onlyCountries={['us']}
              value={getPhoneNumberValue(inputValue as string)}
              disableAreaCodes={true}
              onChange={(event) => {
                onChange(event);
              }}
              sx={{
                width: '100%',
                paddingBottom: '8px',
                paddingTop: '8px',
              }}
            />
          );
        default:
          return (
            <StyledTextField
              defaultValue={inputValue}
              onChange={(event) => {
                onChange(event);
              }}
              placeholder={!isOutlined ? getTranslation(placeholderText) : ''}
              variant={isOutlined ? 'outlined' : 'standard'}
              id={placeholderText}
              disabled={textFieldDisabled}
              onBlur={onBlur as () => void}
              sx={{
                ...(isOutlined ?
                  {
                    borderRadius: theme.spacing(1.5),
                    border: '1px solid var(--Base-Color-Neutral-N15, #E2E2E4)',
                    boxShadow:
             '0px 1px 2px 0px rgba(0, 0, 0, 0.05), 0px 0px 0px 0px rgba(0, 0, 0, 0.00), 0px 0px 0px 0px rgba(0, 0, 0, 0.00)',
                    paddingTop: theme.spacing(0),
                    paddingBottom: theme.spacing(0),
                  } :
                  {}),
                '& .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.grey[200],
                },
              }}
              InputProps={{
                ...(isOutlined ?
                  {
                    sx: {
                      '.MuiOutlinedInput-input': {
                        padding: theme.spacing(1.875),
                      },
                    },
                  } :
                  {}),
              }}
              data-testid={dataTestId}
            />
          );
      }
    case 'multichoice':
      switch (meta?.behavior) {
        case 'autoComplete':
          return groupAutoComplete ? (
            <GroupAutocompleteComponent
              options={options}
              selectedValues={selectedOptions}
              placeholderText={getTranslation(placeholderText)}
              onChange={(newValue) => onChange(newValue)}
              onBlur={onBlur as () => void}
              data-testid={dataTestId}
            />
          ) : (
            <QuestionComponentAutocomplete
              classes={classes}
              inputValue={(inputValue as QuestionOption) || null}
              options={options}
              placeholderText={getTranslation(placeholderText)}
              onChange={(newValue) => onChange(newValue)}
              onBlur={onBlur as () => void}
              calledFromOnboarding={calledFromOnboarding}
              data-testid={dataTestId}
              showAutoCompletePlaceholderText={showAutoCompletePlaceholderText}
            />
          );
        case 'dragAndDrop':
          return (
            <DraggableListComponent
              options={options}
              onChange={onChange}
              data-testid={dataTestId}
            />
          );
        case 'imageOption':
          return (
            <ImageOptionComponent
              options={options}
              onChange={onChange}
              data-testid={dataTestId}
            />
          );
        case 'groupDropdown':
          return (
            <GroupDropdownComponent
              options={options}
              onChange={onChange}
              subTitle={subTitle}
              defaultValues={radioButtonSelectDefaultValues as QuestionOption[]}
              borderStyle={borderStyle}
              calledFromOnboarding={calledFromOnboarding}
              data-testid={dataTestId}
            />
          );
        default:
          return null;
      }
    case 'poll':
      return (
        <PollQuestionComponent
          options={options}
          onClick={onChange}
          data-testid={dataTestId}
        />
      );
    case 'singleSelect':
      switch (meta?.behavior) {
        case 'radioButtonSelect':
          return (
            <RadioGroup
              // eslint-disable-next-line no-nested-ternary
              defaultValue={getDefaultValue(!Array.isArray(radioButtonSelectDefaultValues) ?
                (radioButtonSelectDefaultValues?.value || '') :
                (radioButtonSelectDefaultValues.length === 1 ?
                  radioButtonSelectDefaultValues[0].value || '' :
                  (radioButtonSelectDefaultValues as QuestionOption[])?.find(
                    (item) => item.order === 1,
                  )?.value || ''))}
              onChange={(event) => {
                const selected = options.find((opt) => opt.value === event.target.value);
                onChange?.(selected);
              }}
              sx={{ width: '100%', marginLeft: 1 }}
              data-testid={dataTestId}
            >
              {options.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={
                    <Radio
                      sx={{
                        color: theme.palette.brand.primary,
                        '&.Mui-checked': {
                          color: theme.palette.brand.primary,
                        },
                        ...optionalRadioBtnSelectRadioStyles,
                      }}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Typography variant={optionalRadioBtnSelectFormLabelStyles ? 'body2' : 'h15'}>
                        {option.display}
                      </Typography>
                      <Box
                        sx={{
                          color: `${theme.palette.grey[600]}`,
                          textAlign: 'left',
                          display: 'block',
                          wordBreak: 'break-word',
                        }}
                      >
                        {option.value}
                      </Box>
                    </Box>
                  }
                  sx={{
                    width: '100%',
                    alignItems: 'center',
                    marginBottom: 1.5,
                    padding: 1,
                    border: `1px solid ${theme.palette.grey[200]}`,
                    borderRadius: theme.spacing(1.6),
                    boxSizing: 'border-box',
                    display: 'flex',
                    ...optionalRadioBtnSelectFormLabelStyles,
                  }}
                />
              ))}
            </RadioGroup>
          );
        default:
          return (
            <StyledSelect
              defaultValue={inputValue}
              onChange={onChange}
              sx={{
                width: '100%',
              }}
              MenuProps={styledSelectMenuProps}
              data-testid={dataTestId}
            >
              {options.map((option) => (
                <MenuItem key={option.value} value={option.value} onChange={onChange}>
                  {option.display}
                </MenuItem>
              ))}
            </StyledSelect>
          );
      }
    default:
      return null;
  }
};

export default QuestionComponent;
